import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { MailCheck, ShieldX, UserCheck } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { decideMemberApplication, listMemberApplications } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/members/pending")({
  head: () => ({
    meta: [
      { title: "Pending members — Quibblies" },
      {
        name: "description",
        content:
          "Team review queue for new Quibblies member applications, from email verification through to approval.",
      },
      { property: "og:title", content: "Pending members — Quibblies" },
      { property: "og:description", content: "Review and decide new member applications." },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PendingMembersPage,
});

const STAGES = [
  { key: "pending_email", label: "Verify email" },
  { key: "pending_review", label: "Team review" },
  { key: "approved", label: "Approved" },
] as const;

function StatusTrail({ status }: { status: string }) {
  const activeIndex =
    status === "approved" ? 2 : status === "pending_review" ? 1 : status === "rejected" ? 1 : 0;
  return (
    <ol className="flex flex-wrap items-center gap-2 text-[0.7rem] uppercase tracking-wide">
      {STAGES.map((stage, index) => {
        const done = index < activeIndex;
        const current = index === activeIndex;
        return (
          <li key={stage.key} className="flex items-center gap-2">
            <span
              className={
                done
                  ? "rounded-full bg-emerald-500/15 px-2.5 py-1 text-emerald-300"
                  : current
                    ? "rounded-full bg-primary/20 px-2.5 py-1 text-foreground"
                    : "rounded-full bg-muted/40 px-2.5 py-1 text-muted-foreground"
              }
            >
              {stage.label}
            </span>
            {index < STAGES.length - 1 ? (
              <span aria-hidden="true" className="text-muted-foreground">
                →
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function PendingMembersPage() {
  const queryClient = useQueryClient();
  const load = useServerFn(listMemberApplications);
  const decide = useServerFn(decideMemberApplication);
  const [notes, setNotes] = React.useState<Record<string, string>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-member-applications"],
    queryFn: () => load(),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: (input: { userId: string; decision: "approved" | "rejected"; note: string }) =>
      decide({ data: input }),
    onSuccess: (_result, input) => {
      toast.success(
        input.decision === "approved" ? "Member approved" : "Application rejected",
        { description: "The member has been told, with appeal instructions where relevant." },
      );
      void queryClient.invalidateQueries({ queryKey: ["admin-member-applications"] });
    },
    onError: (e: Error) => toast.error("Could not save the decision", { description: e.message }),
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Member applications"
        description="Sign up → verify email → application pending → team review → approved. Members cannot take part until a person has approved them."
      />

      {isLoading ? (
        <Skeleton className="mt-8 h-64 w-full rounded-2xl" />
      ) : error ? (
        <div className="mt-8">
          <EmptyState
            icon={ShieldX}
            title="Team access required"
            description="Only Quibblies moderators, administrators and the Owner can review applications."
          />
        </div>
      ) : !data?.length ? (
        <div className="mt-8">
          <EmptyState
            icon={UserCheck}
            title="Queue is clear"
            description="Every application has been decided. New sign-ups will appear here once they confirm their email."
          />
        </div>
      ) : (
        <ul className="mt-8 space-y-4">
          {data.map((member) => (
            <li
              key={member.userId}
              className="rounded-2xl border border-border/60 bg-surface/50 p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg text-foreground">{member.displayName}</p>
                  <p className="text-xs text-muted-foreground">
                    @{member.username} · applied {new Date(member.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <Badge variant={member.status === "rejected" ? "destructive" : "outline"}>
                  {member.status.replace("_", " ")}
                </Badge>
              </div>

              <div className="mt-4">
                <StatusTrail status={member.status} />
              </div>

              <dl className="mt-4 grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
                <div>
                  <dt className="uppercase tracking-wide">Date of birth</dt>
                  <dd className="text-foreground">{member.dateOfBirth ?? "Not provided"}</dd>
                </div>
                <div>
                  <dt className="uppercase tracking-wide">Terms</dt>
                  <dd className="text-foreground">
                    {member.acceptedTerms ? "Accepted" : "Not accepted"}
                  </dd>
                </div>
                <div>
                  <dt className="uppercase tracking-wide">Guidelines</dt>
                  <dd className="text-foreground">
                    {member.acceptedGuidelines ? "Accepted" : "Not accepted"}
                  </dd>
                </div>
              </dl>

              {member.appeal ? (
                <div className="mt-4 rounded-xl border border-border/60 bg-background/40 p-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Appeal submitted
                  </p>
                  <p className="mt-1 text-foreground">{member.appeal}</p>
                </div>
              ) : null}

              {member.status === "pending_email" ? (
                <p className="mt-4 flex items-center gap-2 text-xs text-amber-300">
                  <MailCheck className="h-3.5 w-3.5" aria-hidden="true" />
                  Waiting on email verification — you can still approve early if you are satisfied.
                </p>
              ) : null}

              <Textarea
                className="mt-4"
                rows={2}
                placeholder="Reason sent to the member (required for a rejection)"
                value={notes[member.userId] ?? ""}
                onChange={(e) => setNotes((n) => ({ ...n, [member.userId]: e.target.value }))}
              />

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="hero"
                  disabled={mutation.isPending}
                  onClick={() =>
                    mutation.mutate({
                      userId: member.userId,
                      decision: "approved",
                      note: notes[member.userId] ?? "",
                    })
                  }
                >
                  Approve member
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={mutation.isPending || member.status === "rejected"}
                  onClick={() => {
                    const note = notes[member.userId]?.trim() ?? "";
                    if (!note) {
                      toast.error("Add a reason", {
                        description: "Rejected members must be told why, so they can appeal.",
                      });
                      return;
                    }
                    mutation.mutate({ userId: member.userId, decision: "rejected", note });
                  }}
                >
                  Reject
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
